export const post = {
  id: "iq-isnt-intellect",
  title: "IQ Isn't Intellect",
  excerpt:
    "Being fast at thinking isn't the same as thinking well. The people who scare me aren't the slow ones. They're the sharp ones who stopped turning their intelligence back on themselves.",
  image: "/images/blogs/iq-isnt-intellect.jpg",
  category: ["Personal Growth", "Personal Reflection"],
  date: "May 22, 2026",
  readTime: 7,
  slug: "iq-isnt-intellect",
  content: `
For most of my life, I measured intelligence by speed.

How fast you could process something, how much you could hold in your head at once, whether you spotted the pattern first. That felt like what "smart" meant: quicker reflexes for thought.

What I didn't understand then is that IQ measures something narrower than that. It measures processing power. Raw throughput. And processing power, on its own, tells you almost nothing about whether someone's actually thinking or just thinking fast.

## Speed in the Wrong Direction

The real danger shows up when fast processing gets aimed the wrong way.

You don't just land on the wrong conclusion. You land on it with more confidence. Your arguments get tighter. More articulate. Harder for anyone else to dismantle, and harder for you to question, because your own mind is already three steps ahead, building the case before doubt can catch up.

I call this the smart idiot effect.

Someone with high IQ locks onto a bad premise, and suddenly correction barely gets through. Challenges get absorbed and reframed until they disappear. The part of their mind that should be catching errors ends up defending the error instead, polishing it and protecting it.

**They don't get dumber. They get more efficiently wrong.**

A smarter idiot. The problem isn't a lack of ability. It's ability pointed in a direction nobody's willing to redirect.

Dan Kahan found the same pattern in data. Among liberals and conservatives alike, the highest scorers on cognitive tests were the most likely to show political bias. More ability meant more sophisticated defence of what they already believed, not more accuracy.

In a [follow-up study](https://papers.ssrn.com/sol3/papers.cfm?abstract_id=2319993), the pattern got even stranger. People who scored highest in numeracy could evaluate statistical data objectively when it was framed as relating to a skin rash treatment. Same numbers, same study design, but when that identical data was presented as relating to something polarizing like gun control, the highest scorers showed the greatest bias, not the least. The skill was real. It just switched off the moment the conclusion threatened something they cared about.

That's the smart idiot effect with data behind it. Kahan called it motivated numeracy.

## When Critical Thinking Isn't Enough

For a while, I thought I'd figured out the opposite of this. Stupidity isn't low IQ, I told myself. It's a lack of critical thought. Just learn to question things. Poke holes. Demand evidence. That should fix it.

It helps. But it doesn't go far enough.

Most of what we call critical thinking points in one direction, outward. You evaluate someone else's argument. You stress-test their evidence. You find the weak joint in *their* logic. That's a real skill, and it's worth building, but it's still you standing outside the problem, judging from a distance.

Genuine intellect does something harder. It turns around.

Not only *is this argument valid?* but *is the way I'm evaluating it valid?* Not only *are they wrong?* but *what if the framework I'm using to decide who's wrong is what's broken?* That's the second-order move, thinking about your own thinking, and it's where most people simply stop.

You can get very good at taking apart other people's ideas and never once apply the same pressure to the person doing the dismantling. Which is you. Sophisticated enough to critique the room. Not rigorous enough to critique the critic.

## The Loop That Actually Matters

That brought me to a definition of stupidity I didn't expect.

It isn't slow processing. It isn't struggling with maths or reading or never having gone to university. It isn't low IQ in any straightforward sense.

**Stupidity is what happens when thinking never examines itself.**

You can be quick and fluent, technically right on individual claims, and still be stupid in this deeper sense, because the whole operation is running without a governor. No internal check. No moment where the system pauses and asks whether the system itself is doing what it's supposed to do.

The questions that separate the two:

*Am I actually thinking right now?*

*Or am I running a script I committed to before this conversation even started?*

## Knowing When to Stop

There's a trap on the far side of this too.

You can turn inward so relentlessly that you never move. Analyse the analysis. Question the framework for questioning the framework. Spin until action feels irresponsible and every path looks equally suspect.

At some point, recursion has to break. Not because self-examination doesn't matter. It does, constantly. But because intellect isn't only about catching yourself. It's also about committing long enough to find out what happens when you act on what you've learned.

You have to do the work. Build the thing and hold the position long enough to test it, let reality push back.

The loop isn't meant to run forever. It's meant to run often enough that when you finally move, you're steering, not just accelerating.
`,
};
